import { useState, useMemo } from 'react';
import type { ParsedCSV, ShowcaseProspectRequest } from '../../../../types/extraction';
import { createTeamWithPlayers, getTeams } from '../../../../services/teams';
import { checkImportDuplicates } from '../../../../services/prospects';
import {
  DuplicateImportGuard,
  type GuardItem,
  type ImportResolution,
} from './DuplicateImportGuard';
import { toImportCandidate } from './importConflicts';
import {
  sanitizeValue,
  sanitizePhone,
  parseIntOrUndefined,
  sanitizeEmail,
  sanitizeHandedness,
  sanitizePosition,
  sanitizeHeight,
  sanitizeWeight,
  sanitizeGradYear,
} from '../../../../utils/data-sanitizer';
import { readRosterClass, type JucoYear } from '../../../../utils/roster-class';
import { Checkbox } from '../../../../components/Checkbox';
import { JucoYearReview } from './JucoYearReview';
import {
  buildJucoReviewRows,
  fillBlankJucoYears,
  jucoYearsByRowIndex,
  stampJucoOnPlayer,
  unresolvedJucoRows,
  type JucoYearEdits,
} from './jucoImport';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import { ROSTER_IMPORT_FEATURE, ROSTER_IMPORT_TOURS } from './rosterImportTours';
import './MultiTeamReviewStep.css';
import type { ExtractedRow } from "../../../../types/extraction";

interface TeamGroup {
  teamName: string;
  players: ExtractedRow[];
}

interface MultiTeamReviewStepProps {
  mappedData: ParsedCSV;
  /** Seeds every team's JUCO toggle from the up-front declaration on the upload-type step. */
  defaultIsJucoRoster?: boolean;
  /**
   * Originating UploadMetadata id of the roster image this import came from.
   * One uploaded image is the shared source for every team it produces, so the
   * same id is sent for each created team ("most recent roster wins" is
   * per-team, so there is no cross-team clobber).
   */
  rosterUploadId?: string;
  /**
   * The roster extraction (`roster_extractions.id`) these rows were reviewed from.
   * Sent with the commit so each imported prospect records where it came from.
   * Omitted when absent.
   */
  extractionId?: string;
  onComplete: (
    teamCount: number,
    prospectCount: number,
    skippedCount: number,
    duplicateCount: number,
    failedTeams: { teamName: string; error: string }[],
    createdTeamIds: string[]
  ) => void;
  onBack: () => void;
}

function teamNameFromRow(row: ExtractedRow): string {
  const color = sanitizeValue(row.team_color);
  const number = sanitizeValue(row.team_number);
  const teamId = sanitizeValue(row.team_id);

  if (color && number) return `${color} ${number}`;
  if (color) return color;
  if (number) return number;
  if (teamId) return teamId;

  return sanitizeValue(row.school) || 'Unknown';
}

function groupByTeam(data: ParsedCSV): TeamGroup[] {
  const teamMap = new Map<string, ExtractedRow[]>();

  for (const row of data.rows) {
    const teamName = teamNameFromRow(row);

    if (!teamMap.has(teamName)) {
      teamMap.set(teamName, []);
    }
    teamMap.get(teamName)!.push(row);
  }

  return Array.from(teamMap.entries()).map(([teamName, players]) => ({
    teamName,
    players,
  }));
}

function buildPlayers(
  rows: ExtractedRow[],
  jucoYears: Map<number, JucoYear> | null,
): { players: ShowcaseProspectRequest[]; skippedCount: number } {
  const mapped = rows.map((row, rowIndex) => {
    // One read of the cell, both facts kept: the eligibility year is what the roster said,
    // the grad year is inferred from it for the screens that have always shown one. Read once
    // per row so both answers come from the same instant.
    const classReading = readRosterClass(row.class || row.grad_year);

    const player: ShowcaseProspectRequest = {
      firstName: sanitizeValue(row.first_name) || 'Unknown',
      lastName: sanitizeValue(row.last_name) || 'Unknown',
      jerseyNumber: parseIntOrUndefined(row.number),
      position: sanitizePosition(row.position),
      gradYear: sanitizeGradYear(classReading.gradYear ?? undefined),
      eligibilityClass: classReading.eligibilityClass ?? undefined,
      email: sanitizeEmail(row.email),
      phone: sanitizePhone(row.phone),
      height: sanitizeHeight(row.height),
      weight: parseIntOrUndefined(sanitizeWeight(row.weight)),
      bats: sanitizeHandedness(row.bats),
      throws: sanitizeHandedness(row.throws),
      school: sanitizeValue(row.school),
      twitter: sanitizeValue(row.twitter),
      instagram: sanitizeValue(row.instagram),
      city: sanitizeValue(row.city),
      state: sanitizeValue(row.state),
      address: sanitizeValue(row.address),
      zipCode: sanitizeValue(row.zip),
      birthDate: sanitizeValue(row.birth_date),
      parentName: sanitizeValue(row.parent_name),
      parentPhone: sanitizePhone(row.parent_phone),
      coachName: sanitizeValue(row.coach_name),
      coachPhone: sanitizePhone(row.coach_phone),
      advisorName: sanitizeValue(row.advisor_name),
      advisorPhone: sanitizePhone(row.advisor_phone),
      notes: sanitizeValue(row.notes),
    };

    // Stamped before the filter below, and keyed by the index into `rows`, so dropping unnamed
    // rows cannot shift a JC year onto the wrong player.
    return jucoYears
      ? stampJucoOnPlayer(
          player,
          jucoYears.get(rowIndex) ?? null,
      // A stated grad year (a bare "2025") reads as a grad year and no class, so eligibilityClass
      // is null exactly when the roster said the year outright rather than us inferring it.
      { gradYearWasStated: classReading.eligibilityClass === null && classReading.gradYear != null },
        )
      : player;
  });

  const players = mapped.filter((p) => {
    const hasFirst = p.firstName && p.firstName !== 'Unknown' && p.firstName.trim().length > 0;
    const hasLast = p.lastName && p.lastName !== 'Unknown' && p.lastName.trim().length > 0;
    return hasFirst && hasLast;
  });

  return { players, skippedCount: mapped.length - players.length };
}

/** One team's rows, sanitized and ready to send. Built once so guard row indexes stay aligned. */
type PreparedGroup = {
  teamName: string;
  players: ShowcaseProspectRequest[];
  /** Rows dropped for having no usable name. */
  skippedCount: number;
  /** Existing team this name resolves to, when it does. Strengthens duplicate matching. */
  teamId: string | null;
};

/** Guard row id: which team group, and which row within it. */
const guardItemId = (groupIndex: number, rowIndex: number): string => `${groupIndex}:${rowIndex}`;

export function MultiTeamReviewStep({
  mappedData,
  rosterUploadId,
  extractionId,
  onComplete,
  onBack,
  defaultIsJucoRoster = false,
}: MultiTeamReviewStepProps) {
  const initialGroups = useMemo(() => groupByTeam(mappedData), [mappedData]);
  const [editingNames, setEditingNames] = useState<Record<number, string>>(() => {
    const names: Record<number, string> = {};
    initialGroups.forEach((g, i) => { names[i] = g.teamName; });
    return names;
  });
  // Per TEAM, not per batch. A scout book holds a prep program alongside the junior colleges
  // (TCS, p35 of the one Patrick sent), and a batch-level toggle would stamp those players JUCO.
  const [jucoTeams, setJucoTeams] = useState<Record<number, boolean>>(() =>
    // Seeded from the coach's up-front declaration: every team starts marked, and the prep
    // program that shares a scout book with the junior colleges is one untick on its own card.
    // Seed, not control - a correction here must not be undone by a re-render.
    defaultIsJucoRoster
      ? Object.fromEntries(initialGroups.map((_, index) => [index, true]))
      : {},
  );
  const [jucoEditsByTeam, setJucoEditsByTeam] = useState<Record<number, JucoYearEdits>>({});

  // Unnamed rows are dropped from this path's payload, so they are not listed and cannot block.
  const jucoReviewRowsByTeam = useMemo(
    () => initialGroups.map((group) => buildJucoReviewRows(group.players, { skipUnnamedRows: true })),
    [initialGroups],
  );

  const unresolvedJucoTeams = initialGroups
    .map((group, index) => ({
      index,
      teamName: editingNames[index] || group.teamName,
      unresolved: jucoTeams[index]
        ? unresolvedJucoRows(jucoReviewRowsByTeam[index], jucoEditsByTeam[index] ?? {})
        : [],
    }))
    .filter((team) => team.unresolved.length > 0);

  const hasUnresolvedJucoYears = unresolvedJucoTeams.length > 0;

  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [error, setError] = useState('');
  // Duplicate-import guard state. Both are set together when the pre-import
  // check finds matches, and stay null on the common clean-roster path.
  const [guardItems, setGuardItems] = useState<GuardItem[] | null>(null);
  const [guardGroups, setGuardGroups] = useState<PreparedGroup[] | null>(null);

  // The duplicate guard and the import progress screen both replace the team cards, anchors and all.
  useTour(ROSTER_IMPORT_FEATURE, {
    ready: !guardItems && !isImporting,
    hasData: false,
    pageTourKey: ROSTER_IMPORT_TOURS.multiTeamReview,
    dataTourKey: null,
  });

  const totalPlayers = initialGroups.reduce((sum, g) => sum + g.players.length, 0);

  const handleNameChange = (index: number, name: string) => {
    setEditingNames((prev) => ({ ...prev, [index]: name }));
  };

  /**
   * Sanitize every group's rows and resolve each team name to an existing team
   * where one exists. Knowing the target team is what lets the duplicate check
   * recognize a re-import of a roster you already have, so it is worth the one
   * extra request. A failed lookup is not worth blocking on: matching just falls
   * back to name plus grad year and position.
   */
  const prepareGroups = async (): Promise<PreparedGroup[]> => {
    let teamIdsByName = new Map<string, string>();
    try {
      const teams = await getTeams();
      teamIdsByName = new Map(teams.map((t) => [t.name.trim().toLowerCase(), t.id]));
    } catch {
      /* non-blocking: weaker matching, still correct */
    }

    return initialGroups.map((group, index) => {
      const teamName = editingNames[index] || group.teamName;
      // Null rather than an empty map for an unmarked team, so nothing is stamped at all.
      const jucoYears = jucoTeams[index]
        ? jucoYearsByRowIndex(jucoReviewRowsByTeam[index], jucoEditsByTeam[index] ?? {})
        : null;
      const { players, skippedCount } = buildPlayers(group.players, jucoYears);
      return {
        teamName,
        players,
        skippedCount,
        teamId: teamIdsByName.get(teamName.trim().toLowerCase()) ?? null,
      };
    });
  };

  /**
   * Commit every prepared group, one request per team. A team that fails is
   * reported individually rather than aborting the rest of the batch.
   */
  const runImport = async (groups: PreparedGroup[]) => {
    setIsImporting(true);
    setImportProgress(0);
    setError('');

    let totalProspects = 0;
    let successCount = 0;
    let totalSkipped = 0;
    let totalDuplicates = 0;
    const failedTeams: { teamName: string; error: string }[] = [];
    const createdTeamIds: string[] = [];

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      totalSkipped += group.skippedCount;

      try {
        const result = await createTeamWithPlayers({
          team: group.teamName,
          players: group.players,
          // Every team from this upload points at the same source image.
          // Omitted when absent so an empty token is never sent.
          ...(rosterUploadId ? { rosterUploadId } : {}),
          // One extraction produced every team, so each commit names it.
          ...(extractionId ? { extractionId } : {}),
          // Every team here came out of the upload wizard's extraction, reviewed or not.
          entryMethod: 'extraction',
        });

        totalProspects += result.totalProspectsCreated + result.totalProspectsLinked;
        totalDuplicates += result.skippedDuplicates ?? 0;
        successCount++;
        if (result.teamId) {
          createdTeamIds.push(result.teamId);
        }
      } catch (err) {
        failedTeams.push({
          teamName: group.teamName,
          error: getSafeErrorMessage(err),
        });
      }

      setImportProgress(i + 1);
    }

    onComplete(successCount, totalProspects, totalSkipped, totalDuplicates, failedTeams, createdTeamIds);
  };

  const handleImport = async () => {
    setError('');
    setIsImporting(true);

    let groups: PreparedGroup[];
    let flagsByGroup: GuardItem[];

    try {
      groups = await prepareGroups();

      // Check every team's roster against existing players before anything is
      // created. Runs per team because each team is its own import request.
      const results = await Promise.all(
        groups.map((group) =>
          group.players.length === 0
            ? Promise.resolve({ flagged: [] })
            : checkImportDuplicates({
                teamId: group.teamId,
                players: group.players.map(toImportCandidate),
              })
        )
      );

      flagsByGroup = results.flatMap((result, groupIndex) =>
        result.flagged.map((flag) => ({
          id: guardItemId(groupIndex, flag.index),
          groupLabel: groups[groupIndex].teamName,
          flag,
        }))
      );
    } catch (err) {
      // Importing anyway would create the duplicates the check exists to
      // prevent, so stop here and let them retry.
      setIsImporting(false);
      setError(
        `We couldn't check this roster against your existing players: ${getSafeErrorMessage(err)}`
      );
      return;
    }

    if (flagsByGroup.length === 0) {
      await runImport(groups);
      return;
    }

    setGuardGroups(groups);
    setGuardItems(flagsByGroup);
    setIsImporting(false);
  };

  /**
   * Fold the guard's decisions back into the prepared groups, then import.
   * Merged rows carry the existing player's id so the server updates that record
   * and adds it to the team; skipped rows are dropped; "import as new" rows are
   * sent untouched.
   */
  const handleGuardConfirm = async (resolutions: Record<string, ImportResolution>) => {
    if (!guardGroups) return;

    const resolved = guardGroups.map((group, groupIndex) => ({
      ...group,
      players: group.players
        .map((player, rowIndex) => {
          const resolution = resolutions[guardItemId(groupIndex, rowIndex)];
          if (!resolution) return player;
          if (resolution.decision === 'skip') return null;
          if (resolution.decision === 'merge' && resolution.matchProspectId) {
            return {
              ...player,
              linkToProspectId: resolution.matchProspectId,
              ...(resolution.overwriteFields.length > 0
                ? { overwriteFields: resolution.overwriteFields }
                : {}),
            };
          }
          return player;
        })
        .filter((player): player is ShowcaseProspectRequest => player !== null),
    }));

    setGuardItems(null);
    setGuardGroups(null);
    await runImport(resolved);
  };

  const handleGuardCancel = () => {
    setGuardItems(null);
    setGuardGroups(null);
    setError('');
  };

  if (guardItems) {
    return (
      <div className="multi-team-review">
        <DuplicateImportGuard
          items={guardItems}
          onConfirm={handleGuardConfirm}
          onCancel={handleGuardCancel}
          isSubmitting={isImporting}
        />
        {error && <div className="error-message">{error}</div>}
      </div>
    );
  }

  if (isImporting) {
    const pct = initialGroups.length > 0 ? (importProgress / initialGroups.length) * 100 : 0;
    return (
      <div className="multi-team-review">
        <div className="multi-team-importing">
          <div className="spinner" />
          <h3>Importing Teams...</h3>
          <p className="import-progress-text">
            {importProgress} of {initialGroups.length} teams
          </p>
          <div className="import-progress-bar">
            <div className="import-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="multi-team-review">
      <div className="multi-team-header">
        <h3>Review Teams</h3>
        <p className="multi-team-summary">
          {initialGroups.length} teams, {totalPlayers} players total
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="multi-team-list">
        {initialGroups.map((group, index) => (
          <div key={index} className="multi-team-card">
            <div className="multi-team-card-header">
              <span className="player-badge">{group.players.length}</span>
              <input
                type="text"
                className="team-name-input"
                value={editingNames[index] ?? group.teamName}
                onChange={(e) => handleNameChange(index, e.target.value)}
                placeholder="Team name"
                {...(index === 0 ? tourAnchor('roster-import.review.names') : {})}
              />
            </div>
            <p className="player-preview">
              {group.players
                .slice(0, 3)
                .map((p) => `${p.first_name || ''} ${p.last_name || ''}`.trim())
                .join(', ')}
              {group.players.length > 3 ? ` +${group.players.length - 3} more` : ''}
            </p>

            <Checkbox
              checked={jucoTeams[index] ?? false}
              {...(index === 0 ? tourAnchor('roster-import.review.juco') : {})}
              onChange={(e) =>
                setJucoTeams((current) => ({ ...current, [index]: e.target.checked }))
              }
              label={
                <span>
                  <strong>Junior college roster</strong>
                  <span className="toggle-text">
                    {' '}— this team only. Every player is stored as JUCO, at the JC year their
                    Class column names.
                  </span>
                </span>
              }
            />

            {jucoTeams[index] && (
              <JucoYearReview
                idPrefix={`multi-team-${index}`}
                rows={jucoReviewRowsByTeam[index]}
                edits={jucoEditsByTeam[index] ?? {}}
                onChangeRow={(rowIndex, year) =>
                  setJucoEditsByTeam((current) => {
                    const next = { ...(current[index] ?? {}) };
                    if (year) next[rowIndex] = year;
                    else delete next[rowIndex];
                    return { ...current, [index]: next };
                  })
                }
                onFillBlanks={(year) =>
                  setJucoEditsByTeam((current) => ({
                    ...current,
                    [index]: fillBlankJucoYears(
                      jucoReviewRowsByTeam[index],
                      current[index] ?? {},
                      year,
                    ),
                  }))
                }
              />
            )}
          </div>
        ))}
      </div>

      {hasUnresolvedJucoYears && (
        // Import All was never disabled for anything before, so a coach could commit a whole batch
        // with JUCO-marked players carrying no JC year. Naming the teams beats a dead button:
        // the offending rows may be several cards down a long list.
        <div className="error-message">
          {unresolvedJucoTeams
            .map((team) => `${team.teamName} (${team.unresolved.length})`)
            .join(', ')}{' '}
          {unresolvedJucoTeams.length === 1 ? 'has players' : 'have players'} with no JC year. Set
          them on the team's card, or use Apply there to fill them all at once.
        </div>
      )}

      <div className="multi-team-actions">
        <button className="secondary-button" onClick={onBack}>
          Back
        </button>
        <button
          className="primary-button"
          onClick={handleImport}
          disabled={hasUnresolvedJucoYears}
          {...tourAnchor('roster-import.review.import')}
        >
          Import All ({initialGroups.length} Teams)
        </button>
      </div>
    </div>
  );
}
